import React from 'react';
import { FormattedMessage } from 'react-intl';
import currency from '../assets/constants/currency';
import messages from './../text/messages';
import { getProps } from './common';
import { RoleDefinitions } from './roles';

export function getInitials(user) {
    let initials = '';
    if (!user) {
        return 'N/A';
    }

    if (user.first_name && user.first_name.trim().length) {
        initials += user.first_name[0];
    }

    if (user.last_name && user.last_name.trim().length) {
        initials += user.last_name[0];
    }
    return initials;
}

export function uuid(n = 36) {
    return '00000000-0000-4000-8000-000000000000'.replace(/0/g, () => (0 | (Math.random() * 16)).toString(16)).slice(0, n);
}

export function getGreeting() {
    let now = new Date();
    var hours = now.getHours();

    if (hours > 3 && hours < 12) {
        return <FormattedMessage id={'goodMorning'} defaultMessage={'Good morning, '} />;
    } else if (hours >= 12 && hours < 18) {
        return <FormattedMessage id={'goodAfternoon'} defaultMessage={'Good afternoon, '} />;
    } else {
        return <FormattedMessage id={'goodEvening'} defaultMessage={'Good evening, '} />;
    }
}

// note there is no startsWith in IE11
export function beginsWith(s, sub) {
    if (!s || typeof s !== 'string') return false;
    return s.slice(0, sub.length) === sub;
}

// note there is no endsWith in IE11
export function finishesWith(s, sub) {
    if (!s || typeof s !== 'string') return false;
    return s.slice(-sub.length) === sub;
}

export function getColor(string) {
    var hash = 0;
    for (var i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    var color = '#';
    for (var j = 0; j < 3; j++) {
        var value = (hash >> (j * 8)) & 0xff;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
}

export function getAddress(address) {
    if (!address) {
        return undefined;
    }

    let text = address.street ? address.street : undefined;
    text = text ? (address.street2 ? `${text} (${address.street2})` : text) : address.street2;
    text = text ? (address.city ? `${text} ${address.city}` : text) : address.city;
    text = text ? (address.zip ? `${text} ${address.zip}` : text) : address.zip;
    text = text ? (address.state ? `${text} ${address.state}` : text) : address.state;
    text = text ? (address.country ? `${text} ${address.country}` : text) : address.country;

    return text;
}

export function getFullName(user) {
    if (!user || !user.first_name || !user.last_name) {
        return '-';
    }

    return `${user.first_name} ${user.last_name}`;
}

export function getViceVersaFullName(user) {
    if (!user) {
        return '';
    }

    return `${user.last_name}, ${user.first_name}`;
}
/**
 * Retrieve nested item from object/array
 * @param {Object|Array} obj
 * @param {String} path dot separated
 * @param {*} def default value ( if result undefined )
 * @returns {*}
 */
export function getByPath(obj, path, def = undefined) {
    var i, len;
    for (i = 0, path = path.split('.'), len = path.length; i < len; i++) {
        if (!obj || typeof obj !== 'object') return def;
        obj = obj[path[i]];
    }

    return obj === undefined ? def : obj;
}

// to conditionally insert an element into an array, use this: ...ifExists(value).
// it relies on the fact that ...[] will be ignored in array interpolation.
export function ifExists(row) {
    return row ? [row] : [];
}

export function autoFooler(field) {
    return `${field}-${new Date().getTime()}`;
}

export function sortByString(list, property, ascending = true) {
    if (!Array.isArray(list)) {
        return list;
    }

    return list.sort((a, b) => {
        const first = ascending ? a : b;
        const second = ascending ? b : a;
        return (first[property] || '').localeCompare(second[property] || '');
    });
}

export function getCurrency(country = 'US') {
    return currency[country] || 'USD';
}

export function getCurrencySymbol(currency) {
    switch (currency) {
        case 'USD':
            return '$';
        case 'GBP':
            return '£';
        case 'EUR':
            return '€';
        case 'CAD':
            return 'C$';
        case 'BAM':
            return 'KM';
        default:
            return currency || '';
    }
}

export function formatMoney(amount, currency) {
    const numberFormat = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    const amountString = amount.toLocaleString(undefined, numberFormat);
    return `${getCurrencySymbol(currency)}${amountString}`;
}

export function normalizeSSN(ssn) {
    ssn = ssn.replace(/[^X0-9]/g, '');

    if (ssn.length < 4) return ssn;
    ssn = ssn.slice(0, 3) + '-' + ssn.slice(3);
    if (ssn.length < 7) return ssn;
    ssn = ssn.slice(0, 6) + '-' + ssn.slice(6, 10);
    return ssn;
}
export function normalizeNIN(nin) {
    const invalidCharacters = /[^A-Z0-9]+/g;
    nin = String(nin)
        .toUpperCase()
        .replace(invalidCharacters, '');
    if (nin.length > 9) {
        nin = nin.slice(0, 9);
    }
    return nin;
}

export function normalizeCRN(crn) {
    const invalidCharacters = /[^A-Z0-9]+/g;
    crn = String(crn)
        .toUpperCase()
        .replace(invalidCharacters, '');
    if (crn.length > 8) {
        crn = crn.slice(0, 8);
    }
    return crn;
}

export function normalizeUTR(utr) {
    const invalidCharacters = /[^0-9]+/g;
    utr = String(utr)
        .toUpperCase()
        .replace(invalidCharacters, '');
    if (utr.length > 10) {
        utr = utr.slice(0, 10);
    }
    return utr;
}

export function normalizePhoneUS(phone) {
    const numbers = phone
        .replace(/[^0-9]/g, '')
        .replace(/^1/, '')
        .slice(0, 10);
    const areaCode = numbers.slice(0, 3);
    const localNumber = numbers.length > 3 ? numbers.slice(3) : '';
    const exchange = localNumber.slice(0, 3);
    const line = localNumber.length > 3 ? localNumber.slice(3) : '';

    // backspace from "(345) " will be "(345)", which should erase the 5
    if (phone.match(/^\(\d\d\d\)$/)) {
        return `(${areaCode.slice(0, 2)}`;
    }

    if (numbers.length === 0) {
        return '';
    }

    // area code only, "(212)""
    if (numbers.length < 3) {
        return `(${areaCode}`;
    }

    // backspace from "(345) 678 - " will be "(345) 678 -", which should erase the 8
    if (phone.match(/^\(\d\d\d\) \d\d\d -$/)) {
        return `(${areaCode}) ${exchange.slice(0, 2)}`;
    }

    // area code and before the dash, "(212) 345"
    if (numbers.length < 6) {
        return `(${areaCode}) ${exchange}`;
    }

    // area code and before and after dash, "(212) 345 - 6789"
    return `(${areaCode}) ${exchange} - ${line}`;
}

export function maskSSN(ssn) {
    if (!ssn) return ssn;
    if (ssn.indexOf('XXX-XX-') === 0) return ssn;

    ssn = ssn.replace(/\D/g, '');
    if (ssn.length !== 9) return 'invalid SSN';
    return 'XXX-XX-' + ssn.slice(5);
}

export function normalizeEIN(ein) {
    ein = ein.replace(/\D/g, '');
    if (ein.length < 3) return ein;
    ein = ein.slice(0, 2) + '-' + ein.slice(2, 9);
    return ein;
}

export function parseQueryString(queryString) {
    var query = {};
    if (!queryString) {
        return query;
    }

    var pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
    for (var i = 0; i < pairs.length; i++) {
        var pair = pairs[i].split('=');
        query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    }
    return query;
}

export function birthdayToDate(birthday) {
    if (!birthday) {
        return undefined;
    }

    const { year, month, day } = birthday;
    if (!year || !month || !day) {
        return undefined;
    }

    let date = new Date();
    date.setFullYear(year);
    date.setMonth(month);
    date.setDate(day);
    date.setHours(0, 0, 0); // should be in local time zone

    return date;
}

export function birthdayToDateString(date) {
    const { month, day, year } = date;
    if (!month || !day || !year) {
        return undefined;
    }

    let to0Prefixed = item => (item.toString().length === 1 ? `0${item}` : item.toString());

    return `${year}-${to0Prefixed(month)}-${to0Prefixed(day)}`;
}

export function dateToBirthday(value) {
    if (!value) {
        return undefined;
    }

    let date = new Date(value);

    return {
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear()
    };
}

export function resolveRoleName(roleType) {
    let notProvided = <FormattedMessage {...messages.notProvided} />;
    if (!roleType || roleType.trim() === '') {
        return notProvided;
    }

    let role = getProps(RoleDefinitions).find(r => r.type === roleType);
    if (!role) {
        return notProvided;
    }

    return role.label;
}

export function toPhoneString(phone) {
    return phone.indexOf('+1') === 0 ? phone : `+1${phone.replace(/\D/g, '')}`;
}

export function toPhoneFieldString(phone) {
    return phone.indexOf('+1') === 0 ? phone.replace('+1', '') : phone.replace(/\D/g, '');
}

export function isTrue(value) {
    if (typeof value !== 'string') return !!value;
    return ['true', 'yes'].includes(value.toLocaleLowerCase());
}

export function stripTags(msg) {
    if (typeof msg === 'string') return msg;
    if (!msg.props) return msg;
    if (!msg.props.defaultMessage) return msg;

    const s = msg.props.defaultMessage.replace(/<.*?>/g, '');
    const result = { ...msg, props: { ...msg.props, defaultMessage: s } };
    return result;
}



// WEBPACK FOOTER //
// ./src/utilities/string.jsx